import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { enqueueSnackbar, useSnackbar } from 'notistack';
import { BsStripe } from "react-icons/bs";
import { IoIosCloseCircleOutline } from 'react-icons/io';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { formatDate } from '../../utils/utilFuncs';
import { Link } from 'react-router-dom';
import { FaEye, FaPaypal } from "react-icons/fa";
import { CSVLink } from 'react-csv';
import { SiWise } from 'react-icons/si';
import { hostNameBack, today } from '../../utils/constants';
import { Switch } from '@mui/material';
import Loader from '../../utils/Loader';
import Dropdown from '../../components/common/Dropdown';
import GenerateAccountStatement from "../../components/common/GenerateAccountStatement"


const AdminPayments = () => {

    const [stripePublishableKey, setStripePublishableKey] = useState('');
    const [stripeSecretKey, setStripeSecretKey] = useState('');
    const [showAddStripeModel, setShowAddStripeModel] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const token = localStorage.getItem("adminToken");
    const [withdrawalRequests, setWithdrawalRequests] = useState([]);
    const [paymentDate, setPaymentDate] = useState('');
    const [comment, setComment] = useState('');
    const [showMarkPaidModel, setShowMarkPaidModel] = useState(null);
    const [showReleaseModel, setShowReleaseModel] = useState(null);
    const [releaseLoading, setReleaseLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [selectedRequests, setSelectedRequests] = useState([]);
    const [takePaymentMethod, setTakePaymentMethod] = useState("Stripe");
    const [value, setValue] = React.useState(0);

    const [paymentMethods, setPaymentMethods] = useState({
        stripe: true,
        wise: true,
        paypal: true
    });

    const handleChange = (event, newValue) => {
        setValue(newValue);
        setSelectedRequests([]);
    };

    useEffect(() => {

        const fetchStripeKey = async () => {
            try {
                setIsLoading(true);
                const response = await axios.get(`${hostNameBack}/api/v1/settings/admin/keys`, { headers: { Authorization: `Admin ${token}` } });
                if (response.data.success) {
                    setStripePublishableKey(response.data.stripePublishableKey)
                    setStripeSecretKey(response.data.stripeSecretKey)
                    setPaymentMethods(response.data.paymentMethods || { stripe: true, wise: true, paypal: true });
                    setTakePaymentMethod(response.data.takePaymentMethod);
                }
            } catch (error) {
                console.error("Error fetching Stripe keys:", error);
            }
            finally {
                setIsLoading(false);
            }
        };

        const fetchWithdrawalRequests = async (type) => {
            try {
                const response = await axios.get(`${hostNameBack}/api/v1/payments/withdrawal-requests?type=${type}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.success) {
                    setWithdrawalRequests(response.data.withdrawalRequests);
                }
            } catch (error) {
                console.error("Error fetching withdrawal requests:", error);
            }
        };

        if (value === 0) fetchWithdrawalRequests("pendingPayments");
        if (value === 1) fetchWithdrawalRequests("completedPayments");
        if (value === 2) fetchWithdrawalRequests("pendingRefunds");
        if (value === 3) fetchWithdrawalRequests("completedRefunds");
        if (value === 4) fetchWithdrawalRequests("pendingWisePayments");
        if (value === 5) fetchWithdrawalRequests("completedWisePayments");
        if (value === 6) fetchWithdrawalRequests("pendingPaypalPayments");
        if (value === 7) fetchWithdrawalRequests("completedPaypalPayments");
        fetchStripeKey();

    }, [token, value]);


    const handleStripeDetailsSubmit = async (e) => {
        e.preventDefault();

        if (!stripePublishableKey || !stripeSecretKey) {
            enqueueSnackbar('Please fill in both fields', { variant: 'error' });
            return;
        }

        try {
            const response = await axios.post(`${hostNameBack}/api/v1/settings/admin/stripe_keys`,
                { stripePublishableKey, stripeSecretKey },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                enqueueSnackbar('Stripe account details saved successfully!', { variant: 'success' });
                setShowAddStripeModel(false);
            }

        } catch (error) {
            enqueueSnackbar('Failed to save Stripe account details', { variant: 'error' });
            console.error(error);
        }
    };

    const handleToggle = async (method) => {
        const updatedMethods = {
            ...paymentMethods,
            [method]: !paymentMethods[method]
        };
        setPaymentMethods(updatedMethods);
        try {
            await axios.put(`${hostNameBack}/api/v1/settings/admin/payment-methods`,
                { paymentMethods: updatedMethods },
                { headers: { Authorization: `Admin ${token}` } }
            );
            enqueueSnackbar('Payment method updated successfully!', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Failed to update payment method', { variant: 'error' });
            setPaymentMethods(paymentMethods);
        }
    };

    const handleChangeTakePaymentMethod = async (method) => {
        setIsLoading(true);
        setTakePaymentMethod(method);
        try {
            await axios.put(`${hostNameBack}/api/v1/settings/admin/take-payment-method`,
                { takePaymentMethod: method },
                { headers: { Authorization: `Admin ${token}` } }
            );
            enqueueSnackbar('Payment method updated successfully!', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Failed to update payment method', { variant: 'error' });
            setTakePaymentMethod(takePaymentMethod);
        }
        finally {
            setIsLoading(false);
        }
    };

    const handleSelectRequest = (requestId) => {
        setSelectedRequests(prev =>
            prev.includes(requestId)
                ? prev.filter(id => id !== requestId)
                : [...prev, requestId]
        );
    };

    const handleSelectAll = () => {
        if (selectedRequests.length === withdrawalRequests.length)
            setSelectedRequests([]);
        else {
            const allRequestIds = withdrawalRequests.map(request => request._id);
            setSelectedRequests(allRequestIds);
        }
    };

    const markPaidManually = async () => {

        if (!paymentDate || !comment) {
            enqueueSnackbar('Please provide payment date and comment', { variant: 'error' });
            return;
        }

        try {
            const response = await axios.put(`${hostNameBack}/api/v1/payments/mark-paid`, {
                requestIds: selectedRequests,
                paidOn: paymentDate,
                comment,
                isRelease: showMarkPaidModel === "Release" ? true : false
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                enqueueSnackbar(`${selectedRequests.length} payment${selectedRequests.length > 1 ? "s" : ""} marked as Paid!`, { variant: 'success' });
                setWithdrawalRequests(prev => prev.filter(req => !selectedRequests.includes(req._id)));
                setSelectedRequests([]);
                setShowMarkPaidModel(null);
            }

        } catch (error) {
            console.error(error);
            enqueueSnackbar('Something went wrong!', { variant: 'error' });
        }
    }

    const releasePayments = async () => {

        setReleaseLoading(true);

        try {
            const response = await axios.put(`${hostNameBack}/api/v1/payments/release-payment` + (value === 4 ? "/wise" : ""), {
                requestIds: selectedRequests
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                enqueueSnackbar(`${selectedRequests.length} payment${selectedRequests.length > 1 ? "s" : ""} released successfully!`, { variant: 'success' });
                setWithdrawalRequests(prev => prev.filter(req => !selectedRequests.includes(req._id)));
                setSelectedRequests([]);
                setShowReleaseModel(false);
            }
            setReleaseLoading(false);
        } catch (error) {
            enqueueSnackbar(error.reponse?.data?.error || 'Something went wrong!', { variant: 'error' });
            setReleaseLoading(false);
        }
    }

    const refundPayments = async () => {

        setReleaseLoading(true);

        try {
            const response = await axios.put(`${hostNameBack}/api/v1/payments/refund-payment`, {
                requestIds: selectedRequests
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                enqueueSnackbar(`${selectedRequests.length} payment${selectedRequests.length > 1 ? "s" : ""} refunded successfully!`, { variant: 'success' });
                setWithdrawalRequests(prev => prev.filter(req => !selectedRequests.includes(req._id)));
                setSelectedRequests([]);
                setShowReleaseModel(null);
            }
            setReleaseLoading(false);
        } catch (error) {
            enqueueSnackbar(error.response?.data?.error || 'Something went wrong!', { variant: 'error' });
            setReleaseLoading(false);
        }
    }

    const moveToPending = async (type) => {

        try {
            const response = await axios.put(`${hostNameBack}/api/v1/payments/move-to-pending`, { requestIds: selectedRequests, isRefund: type === "Refund" ? true : false }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                enqueueSnackbar(`${selectedRequests.length} payment${selectedRequests.length > 1 ? "s" : ""} moved to Pending Tab!`, { variant: 'success' });
                setWithdrawalRequests(prev => prev.filter(req => !selectedRequests.includes(req._id)));
                setSelectedRequests([]);
            }

        } catch (error) {
            console.error(error);
            enqueueSnackbar('Something went wrong!', { variant: 'error' });
        }
    }

    const calculateTotalAmount = () => {
        return withdrawalRequests
            .filter(req => selectedRequests.includes(req._id))
            .reduce((total, req) => total + req.amount, 0);
    };

    return (
        <div className="adminPaymentsDiv">

            {isLoading && <Loader type="checkmate" />}

            <div className="adminPaymentsContent">

                <div className='adminPaymentMethodsDiv'>
                    <h2 className='secondaryHeading'><span>Payment</span> Methods</h2>
                    <div className="paymentMethods">
                        <div className="method"><BsStripe />Stripe</div>
                        <div>
                            <button className="secondaryBtn" onClick={() => setShowAddStripeModel(true)}>Manage</button>
                            <Switch
                                checked={paymentMethods.stripe}
                                onChange={() => handleToggle('stripe')}
                            />
                        </div>
                    </div>
                    <div className="paymentMethods">
                        <div className="method"><SiWise />Wise</div>
                        <Switch
                            checked={paymentMethods.wise}
                            onChange={() => handleToggle('wise')}
                        />
                    </div>
                    <div className="paymentMethods">
                        <div className="method"><FaPaypal />Paypal</div>
                        <Switch
                            checked={paymentMethods.paypal}
                            onChange={() => handleToggle('paypal')}
                        />
                    </div>
                    {/* <div className='takePaymentDiv'>
                        <h2 className='secondaryHeading'>Take <span>Payments</span> using</h2>
                        <Dropdown options={["Stripe", "Airwallex"]} selected={takePaymentMethod} onSelect={handleChangeTakePaymentMethod} />
                    </div> */}
                </div>

                <div className="paymentsDetails">
                    <Box sx={{ width: '100%' }}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={value} onChange={handleChange} aria-label="basic tabs example" variant="scrollable" scrollButtons="auto">
                                <Tab sx={{ fontSize: "12px" }} label="Stripe - Pending" {...a11yProps(0)} />
                                <Tab sx={{ fontSize: "12px" }} label="Stripe - Completed" {...a11yProps(1)} />
                                <Tab sx={{ fontSize: "12px" }} label="Refunds - Pending" {...a11yProps(2)} />
                                <Tab sx={{ fontSize: "12px" }} label="Refunds - Completed" {...a11yProps(3)} />
                                <Tab sx={{ fontSize: "12px" }} label="Wise - Pending" {...a11yProps(4)} />
                                <Tab sx={{ fontSize: "12px" }} label="Wise - Completed" {...a11yProps(5)} />
                                <Tab sx={{ fontSize: "12px" }} label="Paypal - Pending" {...a11yProps(6)} />
                                <Tab sx={{ fontSize: "12px" }} label="Paypal - Completed" {...a11yProps(7)} />
                            </Tabs>
                        </Box>
                        <CustomTabPanel value={value} index={0}>
                            <PendingPayments
                                requests={withdrawalRequests}
                                selectedRequests={selectedRequests}
                                handleSelectRequest={handleSelectRequest}
                                handleSelectAll={handleSelectAll}
                                setShowMarkPaidModel={setShowMarkPaidModel}
                                setShowReleaseModel={setShowReleaseModel}
                                paymentMethod="Stripe"
                            />
                        </CustomTabPanel>
                        <CustomTabPanel value={value} index={1}>
                            <CompletedPayments
                                requests={withdrawalRequests}
                                selectedRequests={selectedRequests}
                                handleSelectRequest={handleSelectRequest}
                                handleSelectAll={handleSelectAll}
                                moveToPending={moveToPending}
                                paymentMethod="Stripe"
                            />
                        </CustomTabPanel>
                        <CustomTabPanel value={value} index={2}>
                            <PendingRefunds
                                requests={withdrawalRequests}
                                selectedRequests={selectedRequests}
                                handleSelectRequest={handleSelectRequest}
                                handleSelectAll={handleSelectAll}
                                setShowMarkPaidModel={setShowMarkPaidModel}
                                setShowReleaseModel={setShowReleaseModel}
                            />
                        </CustomTabPanel>
                        <CustomTabPanel value={value} index={3}>
                            <CompletedRefunds
                                requests={withdrawalRequests}
                                selectedRequests={selectedRequests}
                                handleSelectRequest={handleSelectRequest}
                                handleSelectAll={handleSelectAll}
                                moveToPending={moveToPending}
                            />
                        </CustomTabPanel>
                        <CustomTabPanel value={value} index={4}>
                            <PendingPayments
                                requests={withdrawalRequests}
                                selectedRequests={selectedRequests}
                                handleSelectRequest={handleSelectRequest}
                                handleSelectAll={handleSelectAll}
                                setShowMarkPaidModel={setShowMarkPaidModel}
                                setShowReleaseModel={setShowReleaseModel}
                                paymentMethod="Wise"
                            />
                        </CustomTabPanel>
                        <CustomTabPanel value={value} index={5}>
                            <CompletedPayments
                                requests={withdrawalRequests}
                                selectedRequests={selectedRequests}
                                handleSelectRequest={handleSelectRequest}
                                handleSelectAll={handleSelectAll}
                                moveToPending={moveToPending}
                                paymentMethod="Wise"
                            />
                        </CustomTabPanel>
                        <CustomTabPanel value={value} index={6}>
                            <PendingPayments
                                requests={withdrawalRequests}
                                selectedRequests={selectedRequests}
                                handleSelectRequest={handleSelectRequest}
                                handleSelectAll={handleSelectAll}
                                setShowMarkPaidModel={setShowMarkPaidModel}
                                setShowReleaseModel={setShowReleaseModel}
                                paymentMethod="Paypal"
                            />
                        </CustomTabPanel>
                        <CustomTabPanel value={value} index={7}>
                            <CompletedPayments
                                requests={withdrawalRequests}
                                selectedRequests={selectedRequests}
                                handleSelectRequest={handleSelectRequest}
                                handleSelectAll={handleSelectAll}
                                moveToPending={moveToPending}
                                paymentMethod="Paypal"
                            />
                        </CustomTabPanel>
                    </Box>
                </div>

            </div>

            {showAddStripeModel && (
                <div className="popupDiv">

                    <div className="popupContent">

                        <div className='form'>
                            <h2 className="secondaryHeading">Add <span>Stripe</span> Details</h2>
                            <div className="horizontalLine"></div>
                            <div className='inputDiv'>
                                <label>Stripe Publishable Key <span>*</span></label>
                                <input
                                    type="text"
                                    value={stripePublishableKey}
                                    onChange={(e) => setStripePublishableKey(e.target.value)}
                                    placeholder="Enter your Stripe Publishable Key"
                                    className='inputField'
                                />
                            </div>
                            <div className='inputDiv'>
                                <label>Stripe Secret Key <span>*</span></label>
                                <input
                                    type="text"
                                    value={stripeSecretKey}
                                    onChange={(e) => setStripeSecretKey(e.target.value)}
                                    placeholder="Enter your Stripe Secret Key"
                                    className='inputField'
                                />
                            </div>
                        </div>

                        <div className="buttonsDiv" style={{ marginTop: "20px" }}>
                            <button type="submit" className='primaryBtn' onClick={handleStripeDetailsSubmit}>Save Stripe Details</button>
                            <button className="secondaryBtn" type="button" onClick={() => setShowAddStripeModel(false)}>Close</button>
                        </div>

                    </div>

                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowAddStripeModel(false)} />
                    </div>

                </div>
            )}

            {showMarkPaidModel && (
                <div className="popupDiv">

                    <div className="popupContent">

                        <div className="form">
                            <div className="inputDiv">
                                <label>Payment Date <span>*</span></label>
                                <input
                                    type="date"
                                    value={paymentDate}
                                    onChange={(e) => setPaymentDate(e.target.value)}
                                    className='inputField'
                                    max={today}
                                />
                            </div>
                            <div className="inputDiv">
                                <label>Comment <span>*</span></label>
                                <textarea
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    placeholder="Enter a comment"
                                    className='inputField'
                                />
                            </div>
                        </div>

                        <div className="buttonsDiv" style={{ marginTop: "20px" }}>
                            <button className="primaryBtn" onClick={markPaidManually}>Mark Paid</button>
                            <button className="secondaryBtn" onClick={() => setShowMarkPaidModel(null)}>Cancel</button>
                        </div>

                    </div>

                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowMarkPaidModel(null)} />
                    </div>

                </div>
            )}

            {showReleaseModel && (
                <div className="popupDiv">
                    <div className="popupContent releasePopupContent">
                        <div className="form">
                            <h2 className="secondaryHeading">Confirm <span>{showReleaseModel === "Release" ? "Releasing" : "Refunding"}</span> Payments - <span>{value === 4 ? "Wise" : "Stripe"}</span></h2>
                            <div className="horizontalLine"></div>
                            <p>You are about to release a total of <strong>${calculateTotalAmount().toFixed(2)}</strong> to the {showReleaseModel === "Release" ? "sellers" : "buyers"}. Please ensure you have sufficient funds in your your linked {value === 4 ? "Wise" : "Stripe"} account!</p>
                        </div>

                        <div className="buttonsDiv" style={{ marginTop: "20px" }}>
                            <button className="primaryBtn" onClick={showReleaseModel === "Release" ? releasePayments : refundPayments} disabled={releaseLoading}>{showReleaseModel === "Release" ? "Release" : "Refund"} ${calculateTotalAmount().toFixed(2)}</button>
                            <button className="secondaryBtn" onClick={() => setShowReleaseModel(null)}>Cancel</button>
                        </div>
                    </div>

                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowReleaseModel(null)} />
                    </div>
                </div>
            )}


        </div>
    );
};

export default AdminPayments;




function PendingPayments({ requests, selectedRequests, handleSelectRequest, handleSelectAll, setShowMarkPaidModel, setShowReleaseModel, paymentMethod }) {

    const areAllSelected = selectedRequests.length === requests.length;
    const [showSellerDetailsModel, setShowSellerDetailsModel] = useState(null);
    const [history, setHistory] = useState([]);
    const [earnings, setEarnings] = useState({
        availableBalance: 0,
        totalEarnings: 0,
        paidBalance: 0,
        requestedForWithdrawal: 0,
        productsSold: 0,
        servicesDone: 0,
    });
    const [messageToRecipient, setMessageToRecipient] = useState("");
    const [showMessageToRecipientModel, setShowMessageToRecipientModel] = useState(false);

    const token = localStorage.getItem("adminToken");

    const showSellerDetails = (sellerId) => {

        setShowSellerDetailsModel(sellerId);

        axios.get(`${hostNameBack}/api/v1/payments/seller/${sellerId}`, {
            headers: { Authorization: `Admin ${token}` }
        })
            .then(response => {
                if (response.data.success)
                    setHistory(response.data.history);
            })
            .catch(e => {
                enqueueSnackbar("Something went wrong with payments", { variant: 'error' });
            });

        axios.get(`${hostNameBack}/api/v1/payments/seller/${sellerId}/earnings`, {
            headers: { Authorization: `Admin ${token}` }
        })
            .then(response => {
                if (response.data.success)
                    setEarnings(response.data.earnings);
            })
            .catch(e => {});
    };

    const paymentElems = requests.length > 0 ? requests.map((request, index) => (
        <div key={index}>
            <div className="requestRow row">
                <div><input
                    type="checkbox"
                    checked={selectedRequests.includes(request._id)}
                    onChange={() => handleSelectRequest(request._id)}
                />
                </div>
                <p className="dateField field">{formatDate(request?.createdAt)}</p>
                <Link to={`/blo-admin/sellers/${request?.userId?.sellerId?._id}`} className="userField field">{request?.userId?.username + " >"}</Link>
                <p className="priceField field">${request?.amount}</p>
                <div className="actionsField field">
                    <FaEye className='icon' onClick={() => showSellerDetails(request?.userId?.sellerId?._id)} />
                </div>
            </div>
            {requests.length > 1 && requests.length - 1 !== index && <div className="horizontalLine"></div>}
        </div>
    ))
        : <div className="row">Nothing to show here...</div>;

    const historyElems = history.length > 0 ? history.map((item, index) => (
        <div key={index}>
            <div className="requestRow row">
                <div className="titleField field">
                    <p className="title">{item?.description}</p>
                </div>
                <p className="dateField field">{formatDate(item?.date)}</p>
                <p className="buyerField field">{item?.buyerUsername || "-"}</p>
                <p className="typeField field">{item?.itemType || "-"}</p>
                <p className="priceField field">${item?.amount}</p>
            </div>
            {history.length > 1 && history.length - 1 !== index && <div className="horizontalLine"></div>}
        </div>
    ))
        : <div className="row">Nothing to show here...</div>;

    const csvData = requests.map(request => ({
        "Requested On": formatDate(request?.createdAt),
        "Seller": request?.userId?.username,
        "Seller ID": request?.userId?._id,
        "Amount": request?.amount
    }));

    const headers = [
        { label: "Requested On", key: "Requested On" },
        { label: "Seller", key: "Seller" },
        { label: "Seller ID", key: "Seller ID" },
        { label: "Amount", key: "Amount" }
    ];

    const csvDataPaypal = requests.map(request => ({
        "Email": request?.userId?.sellerId?.paypalEmail,
        "Amount": request?.amount,
        "Currency code": 'USD',
        "Reference ID (optional)": request?._id,
        "Message to recipient": messageToRecipient,
        "Recipient wallet": 'PayPal',
    }));

    const headersPaypal = [
        { label: "Email", key: "Email" },
        { label: "Amount", key: "Amount" },
        { label: "Currency Code", key: "Currency code" },
        { label: "Reference ID (optional)", key: "Reference ID (optional)" },
        { label: "Message to recipient", key: "Message to recipient" },
        { label: "Recipient wallet", key: "Recipient wallet" }
    ];

    const csvDataWise = requests.map(request => ({
        "name": request?.userId?.sellerId?.wiseAccountName,
        "recipientEmail": request?.userId?.sellerId?.wiseAccountEmail,
        "paymentReference": '',
        "receiverType": "PERSON",
        "amountCurrency": "source",
        "amount": request?.amount,
        "sourceCurrency": 'USD',
        "targetCurrency": 'USD',
        "type": 'EMAIL',
    }));

    const headersWise = [
        { label: "name", key: "name" },
        { label: "recipientEmail", key: "recipientEmail" },
        { label: "paymentReference", key: "paymentReference" },
        { label: "receiverType", key: "receiverType" },
        { label: "amountCurrency", key: "amountCurrency" },
        { label: "amount", key: "amount" },
        { label: "sourceCurrency", key: "sourceCurrency" },
        { label: "targetCurrency", key: "targetCurrency" },
        { label: "type", key: "type" },
    ];

    return (
        <div className="pendingPaymentsDiv tableDiv">
            <div className="tableContent">
                <div className="upper">
                    <h2 className="secondaryHeading"><span>Pending {paymentMethod === "Stripe" ? "Stripe" : paymentMethod === "Wise" ? "Wise" : "Paypal"}</span> Withdrawal requests from Sellers</h2>
                    <div className="upperRight">
                        {(paymentMethod !== "Paypal" && paymentMethod !== "Wise") && <CSVLink data={csvData} headers={headers} filename={`${paymentMethod === "Stripe" ? "pending_stripe_payments" : "pending_wise_payments"}.csv`} className="secondaryBtn">Export CSV</CSVLink>}
                        {paymentMethod === "Paypal" && <button className="secondaryBtn" onClick={() => setShowMessageToRecipientModel(true)}>Export CSV</button>}
                        {paymentMethod === "Wise" && <CSVLink data={csvDataWise} headers={headersWise} filename='pending_wise_payments.csv' className="secondaryBtn">Export CSV</CSVLink>}
                        <button className="secondaryBtn" disabled={selectedRequests.length < 1} onClick={() => setShowMarkPaidModel("Release")}>Mark Paid</button>
                        {(paymentMethod === "Stripe") && <button className="secondaryBtn" disabled={selectedRequests.length < 1} onClick={() => setShowReleaseModel("Release")}>Release Payment</button>}
                    </div>
                </div>
                <div className="header">
                    {requests.length > 0 && <div><input
                        type="checkbox"
                        checked={areAllSelected}
                        onChange={() => handleSelectAll(requests, areAllSelected)}
                    />
                    </div>}
                    <p>Date</p>
                    <p>Seller</p>
                    <p>Amount</p>
                    <p>Actions</p>
                </div>
                <div className="rows">{paymentElems}</div>
            </div>

            {showSellerDetailsModel && (
                <div className="popupDiv">

                    <div className="popupContent">

                        <div className='sellerDetails form'>

                            <h2 className="secondaryHeading"><span>Seller</span> Details</h2>
                            <div className="horizontalLine"></div>

                            <div className="sellerEarnings form rowsParent">
                                <h4 className='fw600'>Earnings</h4>
                                <div className="row">
                                    <p>Total Earnings</p>
                                    <div className="fw600">${earnings.totalEarnings.toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <p>Available Balance</p>
                                    <div className="fw600">${earnings.availableBalance.toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <p>Paid Balance</p>
                                    <div className="fw600">${earnings.paidBalance.toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <p>Requested for Withdrawal</p>
                                    <div className="fw600">${earnings.requestedForWithdrawal.toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <p>Products Sold</p>
                                    <div className="fw600">{earnings.productsSold}</div>
                                </div>
                                <div className="row">
                                    <p>Services Done</p>
                                    <div className="fw600">{earnings.servicesDone}</div>
                                </div>
                            </div>

                            <div className="horizontalLine"></div>

                            <div className="sellerHistory tableDiv">
                                <div className='sellerHistoryUpper'>
                                    <h4 className='fw600'>History</h4>
                                    <GenerateAccountStatement sellerId={showSellerDetailsModel} />
                                </div>
                                <div className="tableContent">
                                    <div className="header">
                                        <p className="title">Activity</p>
                                        <p>Date</p>
                                        <p>Buyer</p>
                                        <p>Item Type</p>
                                        <p>Amount</p>
                                    </div>
                                    <div className="rows">{historyElems}</div>
                                </div>
                            </div>

                        </div>

                        <div className="buttonsDiv" style={{ marginTop: "20px" }}>
                            {/* <button type="submit" className='primaryBtn' onClick={handleSubmit}>Save Stripe Details</button> */}
                            <button className="secondaryBtn" type="button" onClick={() => setShowSellerDetailsModel(null)}>Close</button>
                        </div>

                    </div>

                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowSellerDetailsModel(null)} />
                    </div>

                </div>
            )}

            {showMessageToRecipientModel && (
                <div className="popupDiv">

                    <div className="popupContent">

                        <div className="form">
                            <div className="inputDiv">
                                <label>Message For Recipient</label>
                                <textarea value={messageToRecipient} onChange={(e) => setMessageToRecipient(e.target.value)} className='inputField' placeholder='Enter Message for Recipient' />
                            </div>
                        </div>

                        <div className="buttonsDiv" style={{ marginTop: "20px" }}>
                            <CSVLink data={csvDataPaypal} headers={headersPaypal} filename="pending_paypal_payments.csv" className="secondaryBtn" onClick={() => setShowMessageToRecipientModel(false)}>Generate CSV</CSVLink>
                            <button className="secondaryBtn" type="button" onClick={() => setShowMessageToRecipientModel(false)}>Close</button>
                        </div>

                    </div>

                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowMessageToRecipientModel(false)} />
                    </div>

                </div>
            )}
        </div>
    )
}


function PendingRefunds({ requests, selectedRequests, handleSelectRequest, handleSelectAll, setShowMarkPaidModel, setShowReleaseModel }) {

    const areAllSelected = selectedRequests.length === requests.length;

    // const token = localStorage.getItem("adminToken");

    const paymentElems = requests.length > 0 ? requests.map((request, index) => (
        <div key={index}>
            <div className="requestRow row">
                <div><input
                    type="checkbox"
                    checked={selectedRequests.includes(request._id)}
                    onChange={() => handleSelectRequest(request._id)}
                />
                </div>
                <p className="dateField field">{formatDate(request?.createdAt)}</p>
                <p className="userField field">{request?.userId?.username}</p>
                <p className="priceField field">${request?.amount}</p>
                {/* <div className="actionsField field">
                    <FaEye className='icon' onClick={() => showSellerDetails(request?.userId?.sellerId)} />
                </div> */}
            </div>
            {requests.length > 1 && requests.length - 1 !== index && <div className="horizontalLine"></div>}
        </div>
    ))
        : <div className="row">Nothing to show here...</div>;

    const csvData = requests.map(request => ({
        "Date": formatDate(request?.createdAt),
        "Buyer": request?.userId?.username,
        "Buyer ID": request?.userId?._id,
        "Amount": request?.amount,
    }));

    const headers = [
        { label: "Date", key: "Date" },
        { label: "Buyer", key: "Buyer" },
        { label: "Buyer ID", key: "Buyer ID" },
        { label: "Amount", key: "Amount" }
    ];

    return (
        <div className="pendingPaymentsDiv tableDiv">
            <div className="tableContent">
                <div className="upper">
                    <h2 className="secondaryHeading"><span>Pending</span> Refunds to Buyers</h2>
                    <div className="upperRight">
                        <CSVLink data={csvData} headers={headers} filename={"pending_refunds.csv"} className="secondaryBtn">Export CSV</CSVLink>
                        <button className="secondaryBtn" disabled={selectedRequests.length < 1} onClick={() => setShowMarkPaidModel("Refund")}>Mark Paid</button>
                        <button className="secondaryBtn" disabled={selectedRequests.length < 1} onClick={() => setShowReleaseModel("Refund")}>Refund Payment</button>
                    </div>
                </div>
                <div className="header">
                    {requests.length > 0 && <div><input
                        type="checkbox"
                        checked={areAllSelected}
                        onChange={() => handleSelectAll(requests, areAllSelected)}
                    />
                    </div>}
                    <p>Date</p>
                    <p>Buyer</p>
                    <p>Amount</p>
                    {/* <p>Actions</p> */}
                </div>
                <div className="rows">{paymentElems}</div>
            </div>
        </div>
    )
}



function CompletedPayments({ requests, selectedRequests, handleSelectRequest, handleSelectAll, moveToPending, paymentMethod }) {

    const [showPaymentDetailsModel, setShowPaymentDetailsModel] = useState(null);
    const areAllSelected = selectedRequests.length === requests.length;

    const paymentElems = requests.length > 0 ? requests.map((request, index) => (
        <div key={index}>
            <div className="requestRow row">
                <div><input
                    type="checkbox"
                    checked={selectedRequests.includes(request._id)}
                    onChange={() => handleSelectRequest(request._id)}
                />
                </div>
                <p className="dateField field">{formatDate(request?.createdAt)}</p>
                <p className="dateField field">{formatDate(request?.paidOn)}</p>
                <Link to={`/blo-admin/sellers/${request?.userId?.sellerId?._id}`} className="userField field">{request?.userId?.username + " >"}</Link>
                <p className="typeField field">{request?.paymentType}</p>
                <p className="priceField field">${request?.amount}</p>
                <div className="actionsField field">
                    <FaEye className='icon' onClick={() => setShowPaymentDetailsModel(request)} />
                </div>
            </div>
            {requests.length > 1 && requests.length - 1 !== index && <div className="horizontalLine"></div>}
        </div>
    ))
        : <div className="row">Nothing to show here...</div>;

    const csvData = requests.map(request => ({
        "Requested On": formatDate(request?.createdAt),
        "Paid On": formatDate(request?.paidOn),
        "Seller": request?.userId?.username,
        "Seller ID": request?.userId?.sellerId?._id,
        "Amount": request?.amount,
        "Payment Approach": request?.paymentType,
        "Comment": request?.comment || "-",
    }));

    const headers = [
        { label: "Requested On", key: "Requested On" },
        { label: "Paid On", key: "Paid On" },
        { label: "Seller", key: "Seller" },
        { label: "Seller ID", key: "Seller ID" },
        { label: "Amount", key: "Amount" },
        { label: "Payment Approach", key: "Payment Approach" },
        { label: "Comment", key: "Comment" },
    ];

    return (
        <div className="tableDiv">
            <div className="tableContent">
                <div className="upper">
                    <h2 className="secondaryHeading"><span>Completed {paymentMethod === "Stripe" ? "Stripe" : paymentMethod === "Wise" ? "Wise" : "Paypal"}</span> Withdrawal requests</h2>
                    <div className="upperRight">
                        <CSVLink data={csvData} headers={headers} filename={`${paymentMethod === "Stripe" ? "completed_stripe_payments" : paymentMethod === "Wise" ? "completed_wise_payments" : "completed_paypal_payments"}.csv`} className="secondaryBtn">Export CSV</CSVLink>
                        <button className="secondaryBtn" disabled={selectedRequests.length < 1} onClick={() => moveToPending("Payment")}>Move to Pending</button>
                    </div>
                </div>
                <div className="header">
                    {requests.length > 0 && <div><input
                        type="checkbox"
                        checked={areAllSelected}
                        onChange={() => handleSelectAll(requests, areAllSelected)}
                    />
                    </div>}
                    <p>Requested On</p>
                    <p>Paid On</p>
                    <p>Seller</p>
                    <p>Payed By</p>
                    <p>Amount</p>
                    <p>Actions</p>
                </div>
                <div className="rows">{paymentElems}</div>
            </div>
            {showPaymentDetailsModel && (
                <div className="popupDiv">

                    <div className="popupContent">

                        <div className='sellerDetails form'>

                            <h2 className="secondaryHeading"><span>Payment</span> Details</h2>
                            <div className="horizontalLine"></div>

                            <div className="rowsParent">
                                <div className="row">
                                    <p>Request Made On</p>
                                    <div className="fw600">{formatDate(showPaymentDetailsModel?.createdAt)}</div>
                                </div>
                                <div className="row">
                                    <p>Payment Paid On</p>
                                    <div className="fw600">{formatDate(showPaymentDetailsModel?.paidOn)}</div>
                                </div>
                                <div className="row">
                                    <p>Requested By</p>
                                    <div className="fw600">{showPaymentDetailsModel?.userId?.username}</div>
                                </div>
                                <div className="row">
                                    <p>Amount</p>
                                    <div className="fw600">${showPaymentDetailsModel?.amount}</div>
                                </div>
                                <div className="row">
                                    <p>Payment Approach</p>
                                    <div className="fw600">{showPaymentDetailsModel?.paymentType}</div>
                                </div>
                                {showPaymentDetailsModel?.paymentType === "Manual" && <div className="row">
                                    <p>Comment</p>
                                    <div className="fw600">{showPaymentDetailsModel?.comment}</div>
                                </div>}
                            </div>

                        </div>

                        <div className="buttonsDiv" style={{ marginTop: "20px" }}>
                            <button className="secondaryBtn" type="button" onClick={() => setShowPaymentDetailsModel(null)}>Close</button>
                        </div>

                    </div>

                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowPaymentDetailsModel(null)} />
                    </div>

                </div>
            )}
        </div>
    )
}



function CompletedRefunds({ requests, selectedRequests, handleSelectRequest, handleSelectAll, moveToPending }) {

    const [showPaymentDetailsModel, setShowPaymentDetailsModel] = useState(null);
    const areAllSelected = selectedRequests.length === requests.length;

    const paymentElems = requests.length > 0 ? requests.map((request, index) => (
        <div key={index}>
            <div className="requestRow row">
                <div><input
                    type="checkbox"
                    checked={selectedRequests.includes(request._id)}
                    onChange={() => handleSelectRequest(request._id)}
                />
                </div>
                <p className="dateField field">{formatDate(request?.createdAt)}</p>
                <p className="dateField field">{formatDate(request?.paidOn)}</p>
                <p className="userField field">{request?.userId?.username}</p>
                <p className="typeField field">{request?.paymentType}</p>
                <p className="priceField field">${request?.amount}</p>
                <div className="actionsField field">
                    <FaEye className='icon' onClick={() => setShowPaymentDetailsModel(request)} />
                </div>
            </div>
            {requests.length > 1 && requests.length - 1 !== index && <div className="horizontalLine"></div>}
        </div>
    ))
        : <div className="row">Nothing to show here...</div>;

    const csvData = requests.map(request => ({
        "Order Cancellation Date": formatDate(request?.createdAt),
        "Paid On": formatDate(request?.paidOn),
        "Buyer": request?.userId?.username,
        "Buyer ID": request?.userId?._id,
        "Amount": request?.amount,
        "Payment Approach": request?.paymentType,
        "Comment": request?.comment || "-",
    }));

    const headers = [
        { label: "Order Cancellation Date", key: "Order Cancellation Date" },
        { label: "Paid On", key: "Paid On" },
        { label: "Buyer", key: "Buyer" },
        { label: "Buyer ID", key: "Buyer ID" },
        { label: "Amount", key: "Amount" },
        { label: "Payment Approach", key: "Payment Approach" },
        { label: "Comment", key: "Comment" }
    ];

    return (
        <div className="tableDiv">
            <div className="tableContent">
                <div className="upper">
                    <h2 className="secondaryHeading"><span>Paid</span> Refunds</h2>
                    <div className="upperRight">
                        <CSVLink data={csvData} headers={headers} filename={"completed_refunds.csv"} className="secondaryBtn">Export CSV</CSVLink>
                        <button className="secondaryBtn" disabled={selectedRequests.length < 1} onClick={() => moveToPending("Refund")}>Move to Pending</button>
                        {/* <button className="secondaryBtn" disabled={selectedRequests.length < 1}>Release Payment</button> */}
                    </div>
                </div>
                <div className="header">
                    {requests.length > 0 && <div><input
                        type="checkbox"
                        checked={areAllSelected}
                        onChange={() => handleSelectAll(requests, areAllSelected)}
                    />
                    </div>}
                    <p>Date</p>
                    <p>Paid On</p>
                    <p>Buyer</p>
                    <p>Payed By</p>
                    <p>Amount</p>
                    <p>Actions</p>
                </div>
                <div className="rows">{paymentElems}</div>
            </div>
            {showPaymentDetailsModel && (
                <div className="popupDiv">

                    <div className="popupContent">

                        <div className='sellerDetails form'>

                            <h2 className="secondaryHeading"><span>Payment</span> Details</h2>
                            <div className="horizontalLine"></div>

                            <div className="rowsParent">
                                <div className="row">
                                    <p>Order Cancelled On</p>
                                    <div className="fw600">{formatDate(showPaymentDetailsModel?.createdAt)}</div>
                                </div>
                                <div className="row">
                                    <p>Refund Paid On</p>
                                    <div className="fw600">{formatDate(showPaymentDetailsModel?.paidOn)}</div>
                                </div>
                                <div className="row">
                                    <p>Buyer</p>
                                    <div className="fw600">{showPaymentDetailsModel?.userId?.username}</div>
                                </div>
                                <div className="row">
                                    <p>Amount</p>
                                    <div className="fw600">${showPaymentDetailsModel?.amount}</div>
                                </div>
                                <div className="row">
                                    <p>Payment Approach</p>
                                    <div className="fw600">{showPaymentDetailsModel?.paymentType}</div>
                                </div>
                                {showPaymentDetailsModel?.paymentType === "Manual" && <div className="row">
                                    <p>Comment</p>
                                    <div className="fw600">{showPaymentDetailsModel?.comment}</div>
                                </div>}
                            </div>

                        </div>

                        <div className="buttonsDiv" style={{ marginTop: "20px" }}>
                            <button className="secondaryBtn" type="button" onClick={() => setShowPaymentDetailsModel(null)}>Close</button>
                        </div>

                    </div>

                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowPaymentDetailsModel(null)} />
                    </div>

                </div>
            )}
        </div>
    )
}



function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}